// src/components/coordinateGrid/AxesRenderer.tsx
/**
 * AxesRenderer Component
 * 
 * Renders the X and Y axes with:
 * - Axis lines with optional arrows
 * - Tick marks at intervals
 * - Numeric labels
 * - Axis labels (x, y)
 * - Origin marker
 * 
 * Phase 1: Core infrastructure
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../types/redux';
import { AxisSettings, QuadrantVisibility, AxisLabelFormat } from '../../types/coordinateGrid';

interface AxesRendererProps {
  /** SVG width in pixels */
  width: number;
  /** SVG height in pixels */
  height: number;
  /** Pixels per unit on x-axis */
  scaleX: number;
  /** Pixels per unit on y-axis */
  scaleY: number;
  /** X offset (position of origin in pixels from left) */
  offsetX: number;
  /** Y offset (position of origin in pixels from top) */
  offsetY: number;
  /** Override axis settings */
  axisSettings?: Partial<AxisSettings>;
  /** Override axis color */
  axisColor?: string;
  /** Override axis thickness */
  axisThickness?: number;
  /** Override show origin marker */
  showOriginMarker?: boolean;
  /** Override quadrant visibility */
  quadrantsVisible?: QuadrantVisibility;
  /** Whether dark mode is active */
  isDark?: boolean;
}

// Tick mark configuration
const TICK_LENGTH = 6;
const LABEL_OFFSET = 20;
const AXIS_LABEL_OFFSET = 30;
const ARROW_SIZE = 10;

/**
 * Format a number for display based on format type
 */
function formatValue(value: number, format: AxisLabelFormat): string {
  // Handle zero specially
  if (Math.abs(value) < 0.0001) return '0';
  
  switch (format) {
    case 'integer':
      return Math.round(value).toString();
    case 'decimal':
      // Show up to 2 decimal places, remove trailing zeros
      return parseFloat(value.toFixed(2)).toString();
    case 'fraction':
      // For now, just show decimal - fraction rendering will be enhanced later
      return parseFloat(value.toFixed(2)).toString();
    default:
      return value.toString();
  }
}

/**
 * Generate tick values within a range
 */
function generateTickValues(
  start: number,
  end: number,
  interval: number
): number[] {
  const values: number[] = [];
  const startValue = Math.ceil(start / interval) * interval;
  
  for (let value = startValue; value <= end; value += interval) {
    const roundedValue = Math.round(value * 1000) / 1000;
    values.push(roundedValue);
  }
  
  return values;
}

export const AxesRenderer: React.FC<AxesRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  axisSettings: axisSettingsProp,
  axisColor: axisColorProp,
  axisThickness: axisThicknessProp,
  showOriginMarker: showOriginMarkerProp,
  quadrantsVisible: quadrantsVisibleProp,
  isDark = false,
}) => {
  // Get settings from Redux if not overridden by props
  const coordinateGrid = useSelector((state: RootState) => state.coordinateGrid);
  
  const axisSettings = {
    ...coordinateGrid.axisSettings,
    ...axisSettingsProp,
  };
  const axisColor = axisColorProp ?? (isDark ? '#9CA3AF' : coordinateGrid.axisColor);
  const axisThickness = axisThicknessProp ?? coordinateGrid.axisThickness;
  const showOriginMarker = showOriginMarkerProp ?? coordinateGrid.showOriginMarker;
  const quadrantsVisible = quadrantsVisibleProp ?? coordinateGrid.quadrantsVisible;

  // Label color (slightly muted)
  const labelColor = isDark ? '#D1D5DB' : '#4B5563';
  const axisLabelColor = isDark ? '#E5E7EB' : '#374151';

  // Calculate visible domain in math coordinates
  const domain = useMemo(() => {
    const xMin = -offsetX / scaleX;
    const xMax = (width - offsetX) / scaleX;
    const yMin = -(height - offsetY) / scaleY;
    const yMax = offsetY / scaleY;
    
    return { xMin, xMax, yMin, yMax };
  }, [width, height, scaleX, scaleY, offsetX, offsetY]);

  // Calculate axis bounds based on quadrant visibility
  const axisBounds = useMemo(() => {
    const { q1, q2, q3, q4 } = quadrantsVisible;
    
    // X-axis: show from left to right based on visible quadrants
    let xAxisStart = domain.xMin;
    let xAxisEnd = domain.xMax;
    
    if (!q2 && !q3) xAxisStart = Math.max(0, domain.xMin); // No left quadrants
    if (!q1 && !q4) xAxisEnd = Math.min(0, domain.xMax);   // No right quadrants
    
    // Y-axis: show from bottom to top based on visible quadrants
    let yAxisStart = domain.yMin;
    let yAxisEnd = domain.yMax;
    
    if (!q3 && !q4) yAxisStart = Math.max(0, domain.yMin); // No bottom quadrants
    if (!q1 && !q2) yAxisEnd = Math.min(0, domain.yMax);   // No top quadrants
    
    return { xAxisStart, xAxisEnd, yAxisStart, yAxisEnd };
  }, [domain, quadrantsVisible]);

  // Generate tick marks
  const { xTicks, yTicks } = useMemo(() => {
    const interval = axisSettings.tickInterval;
    
    const xTickValues = generateTickValues(axisBounds.xAxisStart, axisBounds.xAxisEnd, interval);
    const yTickValues = generateTickValues(axisBounds.yAxisStart, axisBounds.yAxisEnd, interval);
    
    return {
      xTicks: xTickValues,
      yTicks: yTickValues,
    };
  }, [axisBounds, axisSettings.tickInterval]);

  // Convert math coordinates to pixel coordinates
  const toPixelX = (value: number) => offsetX + value * scaleX;
  const toPixelY = (value: number) => offsetY - value * scaleY;

  // Calculate axis pixel positions
  const xAxisY = Math.max(0, Math.min(height, offsetY)); // Clamp to viewport
  const yAxisX = Math.max(0, Math.min(width, offsetX));  // Clamp to viewport

  // Determine if axes are visible in viewport
  const xAxisVisible = axisSettings.showXAxis && offsetY >= 0 && offsetY <= height;
  const yAxisVisible = axisSettings.showYAxis && offsetX >= 0 && offsetX <= width;

  // Arrow marker definition
  const arrowMarkerId = 'axis-arrow';

  return (
    <g className="axes-layer">
      {/* Arrow marker definition */}
      <defs>
        <marker
          id={arrowMarkerId}
          markerWidth={ARROW_SIZE}
          markerHeight={ARROW_SIZE}
          refX={ARROW_SIZE - 1}
          refY={ARROW_SIZE / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={`M 0 0 L ${ARROW_SIZE} ${ARROW_SIZE / 2} L 0 ${ARROW_SIZE} Z`}
            fill={axisColor}
          />
        </marker>
      </defs>

      {/* X-Axis */}
      {xAxisVisible && (
        <g className="x-axis">
          {/* Main axis line */}
          <line
            x1={toPixelX(axisBounds.xAxisStart)}
            y1={xAxisY}
            x2={toPixelX(axisBounds.xAxisEnd)}
            y2={xAxisY}
            stroke={axisColor}
            strokeWidth={axisThickness}
            markerEnd={axisSettings.showArrows ? `url(#${arrowMarkerId})` : undefined}
            className="axis-line x-axis-line"
          />

          {/* Tick marks and labels */}
          {xTicks.map((value) => {
            const px = toPixelX(value);
            const isOrigin = Math.abs(value) < 0.0001;
            
            // Skip if outside viewport
            if (px < 0 || px > width) return null;
            
            return (
              <g key={`x-tick-${value}`} className="tick x-tick">
                {/* Tick mark */}
                <line
                  x1={px}
                  y1={xAxisY - TICK_LENGTH / 2}
                  x2={px}
                  y2={xAxisY + TICK_LENGTH / 2}
                  stroke={axisColor}
                  strokeWidth={1}
                />
                
                {/* Label (skip origin if showing origin marker) */}
                {axisSettings.showXLabels && !(isOrigin && showOriginMarker) && (
                  <text
                    x={px}
                    y={xAxisY + LABEL_OFFSET}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={labelColor}
                    fontSize={12}
                    fontFamily="system-ui, sans-serif"
                    className="tick-label x-tick-label"
                  >
                    {formatValue(value, axisSettings.labelFormat)}
                  </text>
                )}
              </g>
            );
          })}

          {/* X-axis label */}
          {axisSettings.xLabel && (
            <text
              x={toPixelX(axisBounds.xAxisEnd) - AXIS_LABEL_OFFSET}
              y={xAxisY - LABEL_OFFSET}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={axisLabelColor}
              fontSize={14}
              fontStyle="italic"
              fontFamily="system-ui, sans-serif"
              className="axis-label x-axis-label"
            >
              {axisSettings.xLabel}
            </text>
          )}
        </g>
      )}

      {/* Y-Axis */}
      {yAxisVisible && (
        <g className="y-axis">
          {/* Main axis line */}
          <line
            x1={yAxisX}
            y1={toPixelY(axisBounds.yAxisStart)}
            x2={yAxisX}
            y2={toPixelY(axisBounds.yAxisEnd)}
            stroke={axisColor}
            strokeWidth={axisThickness}
            markerEnd={axisSettings.showArrows ? `url(#${arrowMarkerId})` : undefined}
            className="axis-line y-axis-line"
          />

          {/* Tick marks and labels */}
          {yTicks.map((value) => {
            const py = toPixelY(value);
            const isOrigin = Math.abs(value) < 0.0001;
            
            // Skip if outside viewport
            if (py < 0 || py > height) return null;
            
            return (
              <g key={`y-tick-${value}`} className="tick y-tick">
                {/* Tick mark */}
                <line
                  x1={yAxisX - TICK_LENGTH / 2}
                  y1={py}
                  x2={yAxisX + TICK_LENGTH / 2}
                  y2={py}
                  stroke={axisColor}
                  strokeWidth={1}
                />
                
                {/* Label (skip origin - shown separately) */}
                {axisSettings.showYLabels && !isOrigin && (
                  <text
                    x={yAxisX - LABEL_OFFSET}
                    y={py}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={labelColor}
                    fontSize={12}
                    fontFamily="system-ui, sans-serif"
                    className="tick-label y-tick-label"
                  >
                    {formatValue(value, axisSettings.labelFormat)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Y-axis label */}
          {axisSettings.yLabel && (
            <text
              x={yAxisX + LABEL_OFFSET}
              y={toPixelY(axisBounds.yAxisEnd) + AXIS_LABEL_OFFSET}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={axisLabelColor}
              fontSize={14}
              fontStyle="italic"
              fontFamily="system-ui, sans-serif"
              className="axis-label y-axis-label"
            >
              {axisSettings.yLabel}
            </text>
          )}
        </g>
      )}

      {/* Origin marker */}
      {showOriginMarker && xAxisVisible && yAxisVisible && (
        <g className="origin-marker">
          {/* Origin circle */}
          <circle
            cx={yAxisX}
            cy={xAxisY}
            r={4}
            fill={isDark ? '#1F2937' : 'white'}
            stroke={axisColor}
            strokeWidth={2}
            className="origin-circle"
          />
          
          {/* Origin label */}
          <text
            x={yAxisX - 12}
            y={xAxisY + 16}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={labelColor}
            fontSize={12}
            fontFamily="system-ui, sans-serif"
            className="origin-label"
          >
            O
          </text>
        </g>
      )}
    </g>
  );
};

export default AxesRenderer;
